import { P2GBox, P2GContainer, P2GGrid, P2GLink, SvgSquareArrowLeft } from '@parcel2go/core-ui';
import DashActions from '~/apps/smartsend/components/dash/DashActions';
import ConsumerActions from './consumer-actions';
import useGoto from '~/hooks/use-goto';
import useLocalStorage from '~/hooks/use-local-storage';
import { P2GMyAccountHeader } from '../header';
import { lazy, Suspense, useContext } from 'react';
import { SmartSendContext } from '~/apps/smartsend/lib/context/SmartSendContext';
import { LocalStorageKeys } from '~/apps/smartsend/lib/consts/LocalStorageKeys';
import useSmartNav from '~/hooks/use-smart-nav';
import { NavSidebar } from './nav-sidebar';
import { useNavExperience } from './nav-experience';
import CdnIconProxy from '~/apps/account-linking/components/CdnIconProxy';
import { SettingsCache } from '~/lib/consts/settings-cache';

const VipDetails = lazy(() => import('./vip'));

type props = {
  children: React.ReactNode;
  subTitle?: string;
  isXs: boolean;
};

export const Navigation = ({ children, subTitle, isXs }: props) => {
  useLocalStorage(LocalStorageKeys.HasVisitedSmartSend, false);
  const { goTo } = useGoto();
  const experience = useNavExperience();
  const { disable, home, isHome, title, maxWidth, showSideBar, currentPageRoute, showNav } = useSmartNav();

  const {
    state: { customerInitialised }
  } = useContext(SmartSendContext);

  return (
    <P2GBox classNames="p2g-myaccount-wrapper">
      {!showSideBar && !disable && SettingsCache.settings.Features.includes('VipSupport') && (
        <Suspense>
          <VipDetails wrapper={(x) => <>{x}</>} />
        </Suspense>
      )}
      {!disable && (
        <P2GMyAccountHeader
          variant="main"
          isXs={isXs}
          title={isXs ? title : undefined}
          subTitle={subTitle}
          hasSidePanel={showSideBar}
          backControl={
            !isHome &&
            home && (
              <P2GLink onClick={() => goTo(home)}>
                <CdnIconProxy size="sm" src="/theme/icons/SquareArrowLeft.svg">
                  <SvgSquareArrowLeft />
                </CdnIconProxy>
              </P2GLink>
            )
          }
          actionControl={
            experience === 'consumer' ? (
              <ConsumerActions isXs={isXs} showNav={showNav} currentPageRoute={currentPageRoute} />
            ) : (
              <DashActions
                currentPageRoute={currentPageRoute}
                isXs={isXs}
                showSmartSendFeatures={customerInitialised}
                showNav={showNav}
              />
            )
          }
        />
      )}
      <div className="p2g-myaccount-content">
        <P2GBox classNames="p2g-dashboard-content-wrapper">
          <P2GBox classNames="p2g-dashboard-content-wrapper-content">
            <P2GGrid container direction="row" wrap="nowrap">
              {showSideBar && (
                <P2GGrid item flexShrink={1}>
                  <NavSidebar currentPageRoute={currentPageRoute} />
                </P2GGrid>
              )}
              <P2GGrid item flexGrow={1}>
                <P2GBox sx={{ minHeight: { sm: '850px' } }} pl={showSideBar ? 2 : 0} pr={showSideBar ? 2 : 0}>
                  {maxWidth === false ? (
                    <P2GBox>{children}</P2GBox>
                  ) : (
                    <P2GContainer maxWidth={maxWidth ?? 'lg'}>
                      <P2GBox mt={isXs ? 2 : 4} mb={isXs ? 2 : 8}>
                        {children}
                      </P2GBox>
                    </P2GContainer>
                  )}
                </P2GBox>
              </P2GGrid>
            </P2GGrid>
          </P2GBox>
        </P2GBox>
      </div>
    </P2GBox>
  );
};
